import { YouTubeConnectionGap } from 'fym-shared/src/smartlinks/artistPortals';
import type { SerializedImageSourceFile } from 'fym-shared/src/music24/imageSourceFile';
/**
 * The Promote DTOs, mirrored from the server contract by hand.
 *
 * The renderer never imports server code, so these shapes are the app's own
 * copy of the wire format. Keep them in step with the REST contract in
 * docs/superpowers/specs/2026-08-25-music24-promote-design.md.
 */

import type { Music24LimitPayload } from 'fym-common/src/errors/music24Limits';
import type {
  CampaignLaunchAnswers,
  ChannelPostingDefaults,
  YouTubePostingDefault,
} from 'fym-shared/src/music24/campaignLaunch';
import type { PostPlatformSettings, PostPlatformSettingsMap } from 'fym-shared/src/music24/postPlatformSettings';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { WaveformShape } from 'fym-shared/src/music24/assetStyles';
import type { ReelVoiceId } from 'fym-shared/src/music24/reelVoices';
import type { XPostsUsage } from 'fym-shared/src/music24/xPosts';

export const socialPlatforms = ['tiktok', 'instagram', 'youtube', 'facebook', 'threads', 'x'] as const;
export type SocialPlatform = (typeof socialPlatforms)[number];

/** Guards a platform read off a url, which is a string until it is checked. */
export function isSocialPlatform(value: string | null): value is SocialPlatform {
  return value !== null && (socialPlatforms as readonly string[]).includes(value);
}

export const socialChannelStatuses = ['manual', 'pendingApproval', 'connected', 'disconnected'] as const;
export type SocialChannelStatus = (typeof socialChannelStatuses)[number];

/** The state of the daily read of a channel (music24-api `SocialChannelSyncStatus`). */
export const socialChannelSyncStatuses = ['idle', 'running', 'ok', 'failed', 'reauthRequired'] as const;
export type SocialChannelSyncStatus = (typeof socialChannelSyncStatuses)[number];

/** What a post is on its platform. The names of `resolvePostFormat` in fym-shared. */
export const socialPostTypes = ['reel', 'short', 'video', 'photo', 'carousel', 'text', 'story'] as const;
export type SocialPostType = (typeof socialPostTypes)[number];

export const promoCampaignKinds = ['release', 'spike', 'evergreen'] as const;
export type PromoCampaignKind = (typeof promoCampaignKinds)[number];

export const promoCampaignStatuses = ['draft', 'live', 'paused', 'done'] as const;
export type PromoCampaignStatus = (typeof promoCampaignStatuses)[number];

export const scheduledPostStatuses = [
  'draft',
  'scheduled',
  'rendering',
  'ready',
  'awaitingManualPost',
  'publishing',
  'posted',
  'failed',
  'canceled',
] as const;
export type ScheduledPostStatus = (typeof scheduledPostStatuses)[number];

/** A feed post, or a 24-hour story. Only Facebook and Instagram take a story. */
export const scheduledPostFormats = ['post', 'story'] as const;
export type ScheduledPostFormat = (typeof scheduledPostFormats)[number];

export type PromoRenderAspect = '9:16' | '1:1';

/** Who the OAuth login connected: the page, channel or account that posts. */
export type SocialChannelConnection = {
  externalId: string;
  displayName: string | null;
  avatarUrl: string | null;
  profileUrl: string | null;
  connectedAt: string | null;
};

/**
 * The two logins of Instagram: `facebook` is the login through a Facebook Page, `instagram` is
 * the sign-in of Instagram itself. Each other platform has one login.
 */
/**
 * The login of a channel. `zernio`: a posting partner holds the login (any of Facebook, Instagram,
 * Threads). The web reads it to draw the card only and never sends a stored value back on
 * Reconnect: only "Sign in with Instagram" sends `instagram`.
 */
export type SocialLoginVariant = 'facebook' | 'instagram' | 'zernio';

/** Where a NEW connect of a platform goes now (`connectVia` of the channel answer). */
export type SocialConnectVia = 'own' | 'partner';

export type SerializedSocialChannel = {
  platform: SocialPlatform;
  handle: string | null;
  status: SocialChannelStatus;
  /** 0 = Sunday. Null while nothing has been learned for this account. */
  bestDayOfWeek: number | null;
  /** "HH:mm" in UTC. Null while nothing has been learned for this account. */
  bestTimeUtc: string | null;
  /**
   * This deployment can connect the platform over OAuth.
   *
   * False for a platform whose app credentials are not set on the server.
   */
  autoPublishAvailable: boolean;
  /**
   * The second login that the card can offer: `instagram` on the Instagram channel when the
   * server can start the sign-in of Instagram itself next to the default login through a
   * Facebook Page. Null in each other case.
   */
  alternativeLogin: 'instagram' | null;
  /**
   * The login that the channel uses. Null for a platform with one login and for a channel with
   * no login.
   */
  loginVariant: SocialLoginVariant | null;
  /**
   * Remote flags of the next connect (owner rule 2026-10-27). Nothing about the posting partner is
   * built into the web: the server says where Connect goes. `partner`: a posting partner holds the
   * login. Optional: an older API does not send it (= `own`).
   */
  connectVia?: SocialConnectVia;
  /** The name of the posting partner of a `partner` connect, for the note on the card. */
  connectPartnerName?: string | null;
  /**
   * True when a new connect goes to the partner and the workspace has no paid plan: the card locks
   * Connect and opens the plan offer (the server would answer 402 `canAutoPostToMeta`).
   */
  connectNeedsPaidPlan?: boolean;
  /** A plain sentence why the channel must sign in again (a move off the partner), else null. */
  reconnectReason?: string | null;
  /**
   * Who the login posts as. Null when no login stands.
   *
   * A platform-side failure keeps it, so the card can say who to reconnect. A
   * disconnect by the artist clears it: the handle stays, the identity does not.
   */
  connection: SocialChannelConnection | null;
  /** Why the last auto-publish or token refresh failed. Set with `disconnected`. */
  connectionError: string | null;
  /** The newest follower count of the daily read. Null: no read yet, or the platform gives none. */
  followers: number | null;
  /** The change of `followers` over the default period of the plan. Null with one count only. */
  followersChange: number | null;
  /** ISO 8601. The end of the last daily read, or null before the first read. */
  lastSyncedAt: string | null;
  syncStatus: SocialChannelSyncStatus;
  /** The platform ended the sign-in, or the sign-in lacks a scope that the read needs. */
  needsReconnect: boolean;
  /** The daily read runs for this channel: it is connected and has the read scopes. */
  canReadNumbers: boolean;
  /**
   * YouTube only: what the one YouTube sign-in lacks, the same value as the Catalog card.
   * Absent on an older server.
   */
  reconnectToAdd?: YouTubeConnectionGap | null;
  /**
   * The stored posting default of this channel (fym-shared `campaignLaunch.ts`). Only YouTube has
   * one, and it holds only the privacy status. Optional: an older API does not send it.
   */
  postingDefaults?: ChannelPostingDefaults | null;
  /**
   * X only: the API posts of this X account in this UTC month (fym-shared `XPostsUsage`). Null
   * when the channel has no login. Absent on the other platforms and on an older API.
   */
  xPosts?: XPostsUsage | null;
  /**
   * False for the placeholder that the API answers for a platform with no saved channel; true for a
   * saved channel, also a manual one with no handle. Absent on an older API (`isCampaignChannel`
   * then falls back to the handle rule).
   */
  saved?: boolean;
  /**
   * True when a post of this channel goes out through the platform API by itself (the server picks
   * the API publisher and the login was not refused). Absent on an older API (`postingModeOf` then
   * uses the client rule).
   */
  publishesByApi?: boolean;
};

/** The numbers of a channel that has no stored row: nothing was read. */
export const emptyChannelNumbers = {
  followers: null,
  followersChange: null,
  lastSyncedAt: null,
  syncStatus: 'idle',
  needsReconnect: false,
  canReadNumbers: false,
} as const;

/** One UTC day of a channel. A number that the platform does not give is null. */
export type SerializedSocialChannelDay = {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
  followers: number | null;
  following: number | null;
  postCount: number | null;
  views: number | null;
  reach: number | null;
};

/** A post that the daily read found on a channel. Music24 published it, or the artist did. */
export type SerializedSocialPost = {
  id: number;
  platform: SocialPlatform;
  type: SocialPostType;
  caption: string | null;
  permalink: string | null;
  thumbnailUrl: string | null;
  /** ISO 8601. */
  publishedAt: string;
  durationSeconds: number | null;
  /** The Music24 post, when Music24 published this post. */
  scheduledPostUuid: string | null;
  /** The latest totals. A number that the platform does not give is null, never 0. */
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  reach: number | null;
  /** The clicks on the links of the post. Only X gives it; null on the other platforms. */
  linkClicks: number | null;
  /** ISO 8601. */
  lastReadAt: string | null;
};

/** The running totals of a post at one UTC day. The app shows the difference between days. */
export type SerializedSocialPostDay = {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  reach: number | null;
  /** The clicks on the links of the post. Only X gives it; null on the other platforms. */
  linkClicks: number | null;
};

/** One page, channel or account a finished login could post as. */
export type SocialConnectTarget = {
  externalId: string;
  displayName: string;
  handle: string | null;
  avatarUrl: string | null;
  profileUrl: string | null;
};

export type SerializedPromoCampaign = {
  uuid: string;
  name: string;
  kind: PromoCampaignKind;
  status: PromoCampaignStatus;
  catalogItemUuid: string | null;
  smartLinkId: number | null;
  startAt: string | null;
  endAt: string | null;
  createdAt: string;
};

export type PromoPostFailCode =
  | 'mediaRequired'
  | 'channelGone'
  | 'loginRefreshFailed'
  | 'mediaTooLarge'
  | 'mediaUnreadable'
  | 'platformTimeout'
  | 'tiktokPrivateAccountOnly'
  | 'tiktokTooManyPosts'
  | 'tiktokBanned'
  | 'tiktokAppDailyCap'
  | 'tiktokPrivacyMismatch'
  | 'tiktokUrlUnverified'
  | 'tiktokReconnect'
  | 'tiktokRateLimited'
  | 'tiktokMediaRejected'
  | 'tiktokMediaPullFailed'
  | 'tiktokSpamRisk'
  | 'tiktokServerError';

export type SerializedScheduledPost = {
  uuid: string;
  campaignUuid: string | null;
  platform: SocialPlatform;
  caption: string;
  mediaFileId: number | null;
  mediaFileUrl: string | null;
  smartLinkId: number | null;
  /** The per-post click attribution code carried on the smartlink url. */
  refCode: string;
  scheduledAt: string;
  status: ScheduledPostStatus;
  format: ScheduledPostFormat;
  postedAt: string | null;
  failReason: string | null;
  /** A failure the app can say in its own words (music24-api `classifyFailReason`), else null. */
  failCode: PromoPostFailCode | null;
  /** The published post on the platform. Set once auto-publishing succeeds. */
  externalPostUrl: string | null;
  /** The settings of the post's platform that the user chose in the Composer, or null. */
  platformSettings: PostPlatformSettings | null;
  /**
   * The post goes out through a platform API whose terms want the user's choice, and it has no
   * settings yet: it cannot be scheduled until the user chooses them ("Needs your settings").
   */
  needsSettings: boolean;
  /**
   * The media of the post is a video with AI-generated footage: the app shows the fixed AI notice
   * (`AiVideoNotice`). Optional: an older API does not send it.
   */
  containsAiVideo?: boolean;
};

export type PromoPerformanceTotals = {
  postsPublished: number;
  clipsRendered: number;
  linkClicks: number;
};

export type PromoPerformanceByChannel = {
  platform: SocialPlatform;
  posts: number;
  linkClicks: number;
};

export type PromoPerformanceTopPost = {
  uuid: string;
  caption: string;
  platform: SocialPlatform;
  linkClicks: number;
  postedAt: string | null;
};

// ---------------------------------------------------------------- endpoints

export type GetPromoChannelsOptions = { accountId: number };
export type GetPromoChannelsResponse = { channels: SerializedSocialChannel[] };

export type PatchPromoChannelOptions = {
  accountId: number;
  platform: SocialPlatform;
  /** `null` clears the handle, which is how a channel is disconnected. */
  handle?: string | null;
  status?: 'manual' | 'disconnected';
  /** "Posting defaults" of the channel (YouTube only); `youtube: null` clears them. */
  postingDefaults?: { youtube: YouTubePostingDefault | null };
};
export type PromoChannelResponse = { channel: SerializedSocialChannel };

export type StartPromoChannelConnectOptions = {
  accountId: number;
  platform: SocialPlatform;
  /**
   * Where the platform sends the browser back to. It must be on the web app's
   * own origin, so the Channels screen sends its own address.
   */
  returnTo?: string;
  /** Instagram only: which of its two logins to start. Absent is the default of the server. */
  loginVariant?: SocialLoginVariant;
};
export type StartPromoChannelConnectResponse = { authorizeUrl: string };

export type GetPromoConnectTargetsOptions = { accountId: number; platform: SocialPlatform };
export type GetPromoConnectTargetsResponse = { targets: SocialConnectTarget[] };

export type SelectPromoConnectTargetOptions = {
  accountId: number;
  platform: SocialPlatform;
  externalId: string;
};

export type DeletePromoChannelConnectionOptions = { accountId: number; platform: SocialPlatform };

export type GetPromoCampaignsOptions = { accountId: number };
export type GetPromoCampaignsResponse = { campaigns: SerializedPromoCampaign[] };

export type CreatePromoCampaignOptions = {
  accountId: number;
  name: string;
  kind: PromoCampaignKind;
  catalogItemUuid?: string;
  smartLinkId?: number;
  startAt?: string;
};
export type PatchPromoCampaignOptions = {
  accountId: number;
  campaignUuid: string;
  name?: string;
  status?: PromoCampaignStatus;
};
export type PromoCampaignResponse = { campaign: SerializedPromoCampaign };

export type GetPromoPostsOptions = {
  accountId: number;
  /** ISO 8601, inclusive. With no range, the server sends every post of the account. */
  from?: string;
  /** ISO 8601, exclusive. */
  to?: string;
};
export type GetPromoPostsResponse = { posts: SerializedScheduledPost[] };

export type CreatePromoPostsOptions = {
  accountId: number;
  channelPlatforms: SocialPlatform[];
  caption: string;
  perChannelCaptions?: Partial<Record<SocialPlatform, string>>;
  mediaFileId?: number;
  smartLinkId?: number;
  campaignUuid?: string;
  /**
   * A feed post by default. A story needs `mediaFileId`, and only Facebook and
   * Instagram accept one — the server rejects the rest.
   */
  format?: ScheduledPostFormat;
  /** ISO 8601, or the literal `'now'`. */
  scheduledAt: string | 'now';
  /**
   * One time per channel, ISO 8601. A platform listed here goes out at its own
   * hour; the rest fall back to `scheduledAt`. This is what lets a campaign
   * stagger five channels in a single call.
   */
  perChannelScheduledAt?: Partial<Record<SocialPlatform, string>>;
  /**
   * Creates the posts as drafts instead of arming them.
   *
   * A draft never dispatches. It is re-armed by editing the post, which is why
   * "Save draft" and "Schedule" are the same call with one flag between them.
   */
  asDraft?: boolean;
  /**
   * The settings that the user chose, one entry per platform. A scheduled post of a platform
   * that needs them, on a connected channel, is refused without them (`M24_POST_SETTINGS_REQUIRED`).
   */
  platformSettings?: PostPlatformSettingsMap;
};
export type CreatePromoPostsResponse = { posts: SerializedScheduledPost[] };

export type PatchPromoPostOptions = {
  accountId: number;
  postUuid: string;
  caption?: string;
  scheduledAt?: string;
  status?: 'canceled';
  /** The settings of the post's own platform. */
  platformSettings?: PostPlatformSettingsMap;
};
export type ConfirmPromoPostPostedOptions = { accountId: number; postUuid: string };
export type MovePromoPostOptions = { accountId: number; postUuid: string; scheduledAt: string };
export type SchedulePromoPostOptions = { accountId: number; postUuid: string };
export type DeletePromoPostOptions = { accountId: number; postUuid: string };
export type SchedulePromoPostsOptions = {
  accountId: number;
  postUuids: string[];
  /** The count the confirm showed. The server answers 409 `M24_SCHEDULE_COUNT_CHANGED` when it differs. */
  expectedCount: number;
};
export type SchedulePromoPostsResponse = { posts: SerializedScheduledPost[] };
/** "Schedule all N posts" of a campaign in one step (fym-shared `campaignLaunch.ts`). */
export type LaunchPromoPostsOptions = {
  accountId: number;
  postUuids: string[];
  /** The one answer of the user for each platform that asked. */
  answers?: CampaignLaunchAnswers;
  /** True: nothing changes; the answer holds the questions to ask first. */
  dryRun?: boolean;
};

/** The causes of a refused post write (music24-api `PromoPostRequestError`). */
export type PromoPostRequestErrorCode =
  | 'M24_POST_TIME_PAST'
  | 'M24_POST_NOT_DRAFT'
  | 'M24_POST_CANCEL_FIRST'
  | 'M24_POST_NOT_DELETABLE'
  | 'M24_SCHEDULE_COUNT_CHANGED'
  | 'M24_POST_SETTINGS_REQUIRED'
  | 'M24_POST_SETTINGS_INVALID'
  | 'M24_TIKTOK_WATERMARK'
  /** 409: the X account used its API posts of this month. The post stays a draft. */
  | 'M24_X_MONTH_LIMIT';

export type GetTikTokCreatorInfoOptions = { accountId: number; mediaFileId?: number };
export type GetYouTubePostingInfoOptions = { accountId: number };

/** `GET promo/media-info`: the AI video flag of the file that the Composer attached. */
export type GetPromoMediaInfoOptions = { accountId: number; mediaFileId: number };

export type GetPromoQuotaOptions = { accountId: number };
/** `limit: null` is no limit. */
export type GetPromoQuotaResponse = { scheduledPost: { used: number; limit: number | null; resetsAt: string } };

// ---------------------------------------------------------------- calendar

/** The kind of the media of a post: a picture, a video, or none (a text post). */
export type CalendarMediaKind = 'image' | 'video';

/** A post of the Calendar: a post with its song and its rollout day. */
export type CalendarPost = SerializedScheduledPost & {
  catalogItemUuid: string | null;
  /** `<kind>.<day key>`, for example `release.outNow`. Null for a post that no rollout made. */
  rolloutKey: string | null;
  mediaKind: CalendarMediaKind | null;
  /** The relative file url of the poster of a video, like `mediaFileUrl`. */
  posterUrl: string | null;
  /**
   * The id of the social post that the daily read linked to this post. "Open in Social" opens
   * `/social/post/:id` with it. It is not the uuid of this post. Null: no Social detail to open.
   */
  socialPostId: number | null;
  /** Why a published post has no `socialPostId`. Null for every other post. */
  socialPostMissing: CalendarSocialPostMissing | null;
};

/**
 * Why a published post has no Social detail: the daily read did not find it yet, it is older
 * than the history of the plan, or it belongs to a platform account that the channel no longer
 * names.
 */
export type CalendarSocialPostMissing = 'notRead' | 'beforeHistory' | 'otherAccount';

/** A song with a post or a release day in the range. */
export type CalendarSong = {
  catalogItemUuid: string;
  title: string;
  artistName: string;
  artworkUrl: string | null;
  /** `sourceUrl` is the imgproxy source of the cover (`drawnFileSrc`); `url` streams the original. */
  artworkFile: SerializedImageSourceFile | null;
  /** `YYYY-MM-DD`. */
  releaseDate: string | null;
  /** Null: the song has no kit. */
  releaseThemeId: ReleaseThemeId | null;
  /** `#rrggbb`, the accent of the release theme. Null: the brand violet. */
  accentColour: string | null;
};

/** One rollout step of a song on a day. */
export type CalendarRolloutDay = {
  catalogItemUuid: string;
  rolloutKey: string;
  kind: PromoCampaignKind;
  dayKey: string;
  /** `YYYY-MM-DD`. */
  date: string;
  isTask: boolean;
  format: 'post' | 'story' | 'reel';
};

export type GetPromoCalendarOptions = {
  accountId: number;
  /** ISO 8601. */
  from: string;
  /** ISO 8601. */
  to: string;
  /** Releases v2: only the posts of this release. */
  catalogItemUuid?: string;
};
export type GetPromoCalendarResponse = {
  posts: CalendarPost[];
  songs: CalendarSong[];
  rolloutDays: CalendarRolloutDay[];
};
export type PromoPostResponse = { post: SerializedScheduledPost };

export type GetPromoPerformanceOptions = { accountId: number };
export type GetPromoPerformanceResponse = {
  totals: PromoPerformanceTotals;
  byChannel: PromoPerformanceByChannel[];
  topPosts: PromoPerformanceTopPost[];
};

/** The period in days. The server limits it to the history window of the plan. */
export type GetPromoChannelStatsOptions = { accountId: number; platform: SocialPlatform; days: number };
export type GetPromoChannelStatsResponse = { days: SerializedSocialChannelDay[] };

export type GetPromoSocialPostsOptions = {
  accountId: number;
  days: number;
  platform?: SocialPlatform;
  type?: SocialPostType;
  /** True: only the posts that Music24 published. */
  ours?: boolean;
  /** The `nextCursor` of the page before. The app sends it back as it got it. */
  cursor?: string;
};
export type GetPromoSocialPostsResponse = { posts: SerializedSocialPost[]; nextCursor: string | null };

export type GetPromoSocialPostStatsOptions = { accountId: number; id: number; days: number };
export type GetPromoSocialPostStatsResponse = { post: SerializedSocialPost; days: SerializedSocialPostDay[] };

export type CreatePromoCaptionsOptions = {
  accountId: number;
  trackTitle: string;
  artistName: string;
  releaseDate?: string;
  angle?: string;
  platforms: SocialPlatform[];
};
export type CreatePromoCaptionsResponse = { captions: Partial<Record<SocialPlatform, string>> };

// ---------------------------- AI Reels ----------------------------
// The wire format of music24-api `src/reels/serializeReel.ts` and `Reels.validators.ts`.

export const reelStatuses = [
  'queued',
  'analysing',
  'planning',
  'rendering',
  'ready',
  'approved',
  'rejected',
  'failed',
] as const;
export type ReelStatus = (typeof reelStatuses)[number];

/** The reel lengths the server takes. */
export const reelDurations = [15, 30] as const;
export type ReelDurationSec = (typeof reelDurations)[number];

/** The reasons a reel fails. The app maps each code to its own copy. */
export type ReelFailReason =
  | 'SONG_TOO_SHORT'
  | 'UNUSABLE_AUDIO'
  | 'ANALYSIS_FAILED'
  | 'DIRECTOR_FAILED'
  | 'BUDGET_EXCEEDED'
  | 'VOICEOVER_FAILED'
  | 'RENDER_FAILED'
  | 'RENDER_TIMEOUT'
  | 'STALLED'
  // The LiteLLM key of Free and trial workspaces has no budget left this month.
  | 'AI_BUDGET_EXHAUSTED';

export type { ReelVoiceId };

/**
 * One reel as the app sees it. The video and poster urls are the relative `files/<uuid>/download`
 * form. `themeId` is the release theme of the song when the reel was made (an old reel reads as
 * Clean): show it through a react-intl message, never the `RELEASE_THEMES` label.
 */
export type SerializedReel = {
  uuid: string;
  status: ReelStatus;
  /** The render progress, 0..100, while the reel renders. */
  progress: number;
  durationSec: number;
  /**
   * Releases v2 (owner answer 3): the reel asset of the song, `15s` or `30s`. The reels of one song
   * and variant are the revisions of one asset. Absent in an older answer: read `${durationSec}s`.
   */
  variant?: string;
  /** The revision inside the song and variant (r1, r2). Absent in an older answer. */
  revision?: number;
  /** The member who started the reel; null for an old reel or a child reel. */
  createdBy?: { id: number; email: string } | null;
  themeId: ReleaseThemeId;
  /** The waveform option of the reel. */
  waveform: WaveformShape;
  voiceId: ReelVoiceId | null;
  /** The Instructions that the user wrote. Null (or absent on an old answer): the default text. */
  instructions?: string | null;
  /**
   * True for a preview render of the Free plan or the trial: half size, a low bit rate and the
   * watermark. Absent in an older answer.
   */
  preview?: boolean;
  /**
   * True when the video carries the Music24 watermark. Absent in an answer of a server that does
   * not send it: `fileHasWatermark` then reads `preview`.
   */
  watermark?: boolean;
  /** The pixel size of the video (540 x 960 for a preview). Absent in an older answer. */
  width?: number;
  height?: number;
  catalogItemUuid: string | null;
  parentReelUuid: string | null;
  createdAt: string;
  /** The last change of the row (status, progress). */
  updatedAt: string;
  failReason: ReelFailReason | null;
  rejectReason: string | null;
  /** True for a `rejected` reel with no child: the app offers "Make another" (regenerate). */
  canRegenerate: boolean;
  video: { fileId: number; url: string; posterUrl: string | null } | null;
  /** The size of the reel video in bytes; null with no video (absent on an older API). */
  videoSizeBytes?: number | null;
  summary: { captions: string[]; voiceoverScript: string | null; caption: string } | null;
  scheduledPostUuid: string | null;
};

/** The `cause` of a refused reel: 402 quota, 403 feature, 503 bank too small. */
export type ReelRefusalCause = Music24LimitPayload | { code: 'AI_REELS_UNAVAILABLE' };

/** The `cause` of a 409: the reel is in a state that does not allow the action. */
export type ReelConflictCause = { code: 'REEL_NOT_READY' | 'REEL_NOT_REGENERATABLE'; status: string };

export type GetReelsOptions = { accountId: number; catalogItemUuid?: string };
export type GetReelsResponse = { reels: SerializedReel[] };

export type GetReelOptions = { accountId: number; reelUuid: string };
export type ReelResponse = { reel: SerializedReel };

/** `GET /accounts/:id/credits` (design g7). `limit` is null when the plan has no monthly cap. */
/** The song is the master of the catalog row (`catalogItemUuid`); the server reads it (D2). */
export type CreateReelOptions = {
  accountId: number;
  catalogItemUuid: string;
  /** Absent: the server uses the lyrics of the catalog row. */
  lyrics?: string;
  /**
   * Not sent any more (design z6, owner answer 2 of 2026-10-08): the AI reel has no waveform, no
   * voiceover and one length of 15 s. The server accepts and ignores them from an old client.
   */
  waveform?: WaveformShape;
  voiceId?: ReelVoiceId | null;
  durationSec?: ReelDurationSec;
  /** The Instructions that the user edited. Absent: the default text of the AI reel in the theme. */
  instructions?: string;
};

export type ApproveReelOptions = {
  accountId: number;
  reelUuid: string;
  /** An empty caption uses the caption the director wrote. */
  caption: string;
  channelPlatforms: SocialPlatform[];
};
export type ApproveReelResponse = { reel: SerializedReel; posts: SerializedScheduledPost[] };

export type RejectReelOptions = { accountId: number; reelUuid: string; reason?: string };

export type RegenerateReelOptions = {
  accountId: number;
  reelUuid: string;
  /** A new reason for the director. Blank or missing: the server uses the reason stored at reject. */
  reason?: string;
};

/** The answer of reject and regenerate when the new reel started. */
export type ReelWithChildResponse = { reel: SerializedReel; child: SerializedReel };

/**
 * The error body of a reject whose new reel was refused (402, 403, 503). The parent stays
 * `rejected`, and `cause` tells why no new reel started.
 */
export type RejectReelRefusedBody = { reel: SerializedReel; child: null; cause: ReelRefusalCause };
